import React, { useEffect, useMemo, useState } from "react";
import {
  App, Breadcrumb, Button, Card, Empty, Flex, Result, Select, Statistic, Tag, Typography, theme
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import useInvoiceProfiles from "../hooks/useInvoiceProfiles";
import useTransactions from "../hooks/useTransactionts";
import type { ITransactions } from "src/types/types";
import FiltersBar from "src/components/transactions/FiltersBar";
import TransactionsTable from "src/components/transactions/TransactionsTable";
import TxDetailDrawer from "src/components/transactions/TxDetailDrawer";
import { currencyFmt } from "src/components/transactions/helpers";
import { useLanguage } from "src/providers/LanguageProvider";

const { Title, Text, Link } = Typography;

export default function TransactionsPage() {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { t } = useLanguage();

  const invoices = useInvoiceProfiles();
  const tx = useTransactions();

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"all" | "paid" | "unpaid">("all");
  const [currency, setCurrency] = useState<"all" | string>("all");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState<ITransactions | null>(null);

  useEffect(() => { invoices.fetchInvoiceProfiles(); }, []);
  useEffect(() => {
    if (invoices.selectedProfileId) tx.fetchTransactions(invoices.selectedProfileId);
  }, [invoices.selectedProfileId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tx.transactions.filter((r) => {
      const matchesText =
        !q ||
        r.RelationName?.toLowerCase().includes(q) ||
        r.TransactionNumber?.toLowerCase().includes(q) ||
        r.DocumentNumber?.toLowerCase().includes(q);

      const matchesStatus =
        status === "all"
          ? true
          : status === "paid"
            ? Number(r.TotalSell || 0) > 0 && Number(r.UnpaidAmount || 0) <= 0
            : Number(r.UnpaidAmount || 0) > 0;

      const matchesCurrency =
        currency === "all" ? true : (r.Currency || "").toUpperCase() === currency.toUpperCase();

      const matchesDate =
        !dateRange ||
        (!dateRange[0] && !dateRange[1]) ||
        (() => {
          const d = dayjs(r.DocumentDate);
          const [start, end] = dateRange;
          if (start && d.isBefore(start.startOf("day"))) return false;
          if (end && d.isAfter(end.endOf("day"))) return false;
          return true;
        })();

      return matchesText && matchesStatus && matchesCurrency && matchesDate;
    });
  }, [tx.transactions, query, status, currency, dateRange]);

  const totals = useMemo(() => {
    const sum = (arr: ITransactions[], selector: (x: ITransactions) => number) =>
      arr.reduce((acc, x) => acc + selector(x), 0);
    const total = sum(filtered, (r) => Number(r.TotalSell || 0));
    const paid = sum(filtered, (r) => Number(r.PaidAmount || 0));
    const unpaid = sum(filtered, (r) => Number(r.UnpaidAmount || 0));
    const cur = filtered[0]?.Currency || "EUR";
    return { total, paid, unpaid, cur };
  }, [filtered]);

  const profileOptions = useMemo(
    () =>
      invoices.invoiceProfiles.map((p) => ({
        value: p.Id,
        label: (
          <Flex align="center" gap={8}>
            <span>{p.CompanyName || `#${p.Id}`}</span>
            <Tag color={p.IsActive ? "green" : "red"}>
              {p.IsActive ? t("ACTIVE") : t("INACTIVE")}
            </Tag>
          </Flex>
        ),
      })),
    [invoices.invoiceProfiles, t]
  );

  const currencyOptions = useMemo(() => {
    const set = new Set<string>();
    tx.transactions.forEach((r) => r.Currency && set.add(r.Currency.toUpperCase()));
    return ["all", ...Array.from(set)];
  }, [tx.transactions]);

  if (invoices.error) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="error"
          title={t("INVOICE_PROFILES_LOAD_FAILED_TITLE")}
          subTitle={invoices.error}
          extra={<Button onClick={() => invoices.fetchInvoiceProfiles()}>{t("BTN_TRY_AGAIN")}</Button>}
        />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100svh", background: token.colorBgLayout }}>
      <Card
        headStyle={{ padding: "12px 16px", borderBottom: "1px solid " + token.colorSplit }}
        bodyStyle={{ padding: 16 }}
        title={
          <Flex align="center" gap={12} wrap>
            <Title level={3} style={{ margin: 0, fontWeight: 700 }}>{t("MY_INVOICES")}</Title>
            <Select
              loading={invoices.loading}
              placeholder={t("PLACEHOLDER_SELECT_INVOICE_PROFILE")}
              value={invoices.selectedProfileId ?? undefined}
              onChange={(v) => invoices.setSelectedProfileId(v)}
              style={{ minWidth: 260 }}
              options={profileOptions as any}
            />
            <Button
              onClick={() => {
                if (invoices.selectedProfileId) tx.fetchTransactions(invoices.selectedProfileId);
                else invoices.fetchInvoiceProfiles();
              }}
            >
              {t("BTN_REFRESH")}
            </Button>
          </Flex>
        }
      >
        <div style={{ marginBottom: 12 }}>
          <FiltersBar
            query={query}
            onQuery={setQuery}
            status={status}
            onStatus={setStatus}
            currency={currency}
            onCurrency={setCurrency}
            dateRange={dateRange}
            onDateRange={setDateRange}
            currencyOptions={currencyOptions}
            loading={tx.loading || invoices.loading}
            onRefresh={() => {
              if (invoices.selectedProfileId) tx.fetchTransactions(invoices.selectedProfileId);
              else invoices.fetchInvoiceProfiles();
            }}
          />
        </div>

        <Flex gap={12} wrap style={{ marginBottom: 12 }}>
          <Card bordered style={{ flex: 1, minWidth: 220 }}>
            <Statistic
              title={t("TOTAL_AMOUNT")}
              value={totals.total}
              precision={2}
              suffix={totals.cur.toUpperCase()}
              valueRender={() => <Text strong>{currencyFmt(totals.total, totals.cur)}</Text>}
            />
          </Card>
          <Card bordered style={{ flex: 1, minWidth: 220 }}>
            <Statistic
              title={t("PAID")}
              value={totals.paid}
              precision={2}
              suffix={totals.cur.toUpperCase()}
              valueRender={() => <Text strong>{currencyFmt(totals.paid, totals.cur)}</Text>}
            />
          </Card>
          <Card bordered style={{ flex: 1, minWidth: 220 }}>
            <Statistic
              title={t("UNPAID")}
              value={totals.unpaid}
              precision={2}
              suffix={totals.cur.toUpperCase()}
              valueRender={() => <Text strong>{currencyFmt(totals.unpaid, totals.cur)}</Text>}
            />
          </Card>
        </Flex>

        <TransactionsTable
          data={filtered}
          loading={tx.loading || invoices.loading}
          onView={(r) => { setSelectedTx(r); setDetailOpen(true); }}
          onPdf={() => message.info(t("PDF_ENDPOINT_NOT_SET"))}
        />

        {(!filtered.length && invoices.selectedProfileId) ? (
          <Empty style={{ marginTop: 16 }} description={t("NO_TRANSACTIONS")} />
        ) : null}
      </Card>

      <TxDetailDrawer open={detailOpen} onClose={() => setDetailOpen(false)} tx={selectedTx} />
    </div>
  );
}
